import { Component, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  PoBreadcrumb,
  PoNotificationService,
  PoUploadComponent,
  PoUploadFileRestrictions
} from '@po-ui/ng-components';
import { Router } from '@angular/router';
import { DpRubricasService } from './dp-rubricas.service';

type ArquivoUpload = { name: string; rawFile?: File };

@Component({
  selector: 'app-dp-descobrir-rubricas',
  templateUrl: './dp-descobrir-rubricas.component.html',
  styleUrls: ['./dp-descobrir-rubricas.component.css'],
  standalone: false
})
export class DpDescobrirRubricasComponent {
  @ViewChild('uploadEsocial') uploadEsocial?: PoUploadComponent;

  codiEmp: number | null = null;
  gerando = false;

  readonly urlPlaceholder = 'https://po-ui.io';
  readonly esocialRestrictions: PoUploadFileRestrictions = {
    allowedExtensions: ['.xml', '.zip'],
    maxFileSize: 180 * 1024 * 1024
  };

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'DP' },
      { label: 'Cadastro de rubricas', link: '/dp-rubricas' },
      { label: 'Descobrir rubricas' }
    ]
  };

  constructor(
    private readonly rubricasService: DpRubricasService,
    private readonly poNotificationService: PoNotificationService,
    private readonly router: Router
  ) {}

  gerarPlanilha(): void {
    if (this.gerando) return;

    if (!this.codiEmp || this.codiEmp <= 0) {
      this.poNotificationService.warning('Selecione a empresa.');
      return;
    }

    const arquivos = this.arquivos();
    if (!arquivos.length) {
      this.poNotificationService.warning('Envie os XMLs ou o .zip do eSocial.');
      return;
    }

    this.gerando = true;
    this.rubricasService.gerarPlanilhaEsocial(this.codiEmp, arquivos).subscribe({
      next: response => {
        this.gerando = false;
        const blob = response.body;
        if (!blob || blob.size === 0) {
          this.poNotificationService.error('A planilha voltou vazia.');
          return;
        }
        this.baixar(blob, this.nomeArquivo(response.headers.get('content-disposition'), this.codiEmp!));
        this.poNotificationService.success(
          'Planilha gerada. Complete o que faltar e importe na tela de cadastro de rubricas.'
        );
        this.uploadEsocial?.clear();
      },
      error: err => {
        this.gerando = false;
        void this.mensagemErro(err).then(mensagem => this.poNotificationService.error(mensagem));
      }
    });
  }

  irParaCadastro(): void {
    void this.router.navigate(['/dp-rubricas']);
  }

  private arquivos(): File[] {
    const files = (this.uploadEsocial?.currentFiles ?? []) as ArquivoUpload[];
    return files.map(item => item.rawFile).filter((file): file is File => !!file);
  }

  private baixar(blob: Blob, nome: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();
    URL.revokeObjectURL(url);
  }

  private nomeArquivo(contentDisposition: string | null, codiEmp: number): string {
    const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(contentDisposition ?? '');
    if (!match) return `rubricas_esocial_${codiEmp}.xlsx`;
    return decodeURIComponent(match[1].replace(/"/g, ''));
  }

  private async mensagemErro(err: HttpErrorResponse): Promise<string> {
    const body = err?.error;
    if (body instanceof Blob) {
      const texto = await body.text();
      try {
        const json = JSON.parse(texto) as { error?: string; Error?: string };
        return json.error || json.Error || texto;
      } catch {
        return texto || 'Não foi possível gerar a planilha.';
      }
    }
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      const json = body as { error?: string; Error?: string; title?: string };
      return json.error || json.Error || json.title || 'Não foi possível gerar a planilha.';
    }
    return 'Não foi possível gerar a planilha.';
  }
}
